import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Group,
  Stack,
  Box,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { isMobile as isMobileDevice } from "react-device-detect";
import DataCollectionIntro from "./data-collection-intro";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";
import { notifications } from "@mantine/notifications";
import { normalizeDob } from "../utils/validateDob";
import useLocale from "../hooks/useLocale";

const USE_SINGLE_PAGE_COPPA_FORM = true;

const CoppaForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  onApiResponse,
}) => {
  const { data, text, dispatch, settings, status, postPayloadMessage } = useContext(DataContext);
  const locale = useLocale("coppa");
  const shouldUseSinglePage = USE_SINGLE_PAGE_COPPA_FORM && data?.edge === true;
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasReceivedFailedResponse, setHasReceivedFailedResponse] = useState(false);
  const [showIntro, setShowIntro] = useState(!shouldUseSinglePage);
  const [showMoreFields, setShowMoreFields] = useState(shouldUseSinglePage);
  const [shouldShowRetryAlert, setShouldShowRetryAlert] = useState(false);
  const inputSize = useAdjustInputSize();
  const isMobileSinglePage = shouldUseSinglePage && isMobileDevice;
  const horizontalPadding = isMobileSinglePage
    ? 16
    : inputSize === "md"
    ? 16
    : 12;
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const NAVIGATION_HEIGHT = 0;
  const additionalFieldsRef = useRef(null);
  const [additionalHeight, setAdditionalHeight] = useState(0);
  const nameFieldsRef = useRef(null);
  const [nameFieldsHeight, setNameFieldsHeight] = useState(0);
  const [nameFieldsVisible, setNameFieldsVisible] = useState(false);
  const bounceTransition = "cubic-bezier(0.68, -0.55, 0.27, 1.55)";
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);
  const retryNotificationId = "coppa-retry-notification";
  const headerIcon = useMemo(() => {
    if (!icon) {
      return null;
    }

    if (React.isValidElement(icon)) {
      const existingStyle = icon.props?.style || {};
      return React.cloneElement(icon, {
        size: icon.props?.size
          ? Math.min(icon.props.size, isMobileSinglePage ? 40 : 32)
          : isMobileSinglePage
          ? 40
          : 28,
        style: { display: "block", ...existingStyle },
      });
    }

    return icon;
  }, [icon, isMobileSinglePage]);

  useEffect(() => {
    if (shouldUseSinglePage) {
      setShowIntro(false);
      setShowMoreFields(true);
    }
  }, [shouldUseSinglePage]);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  const initialVisibleFields = config.initiallyVisibleFields || config.fields.length;

  // Group fields for expandable sections
  const groupedFields = config.fields.reduce(
    (acc, field) => {
      if (field.group) {
        if (!acc.grouped[field.group]) {
          acc.grouped[field.group] = [];
        }
        acc.grouped[field.group].push(field);
      } else {
        acc.ungrouped.push(field);
      }
      return acc;
    },
    { grouped: {}, ungrouped: [] },
  );

  const primaryFields = groupedFields.ungrouped.slice(0, initialVisibleFields);
  const additionalFields = groupedFields.ungrouped.slice(initialVisibleFields);

  // Validation functions
  const isNameFieldsValid = () => {
    const nameFields = config.fields.filter(
      (field) => field.name === "fn" || field.name === "ln",
    );
    return nameFields.every((field) => {
      const value = form.values[field.name] || "";
      return field.validation ? !field.validation(value) : true;
    });
  };

  const isCoppaDetailsValid = () => {
    if (!showMoreFields) return true;
    const isDobComplete = form.values.dob?.length === 8 && !form.errors.dob;
    const isSsnComplete = form.values.ssn?.length === 4 && !form.errors.ssn;
    return isDobComplete && isSsnComplete;
  };

  // Handle expandable animations
  useEffect(() => {
    if (additionalFieldsRef.current) {
      setAdditionalHeight(additionalFieldsRef.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    if (showMoreFields && additionalFieldsRef.current) {
      setAdditionalHeight(additionalFieldsRef.current.scrollHeight);
    }
  }, [showMoreFields]);

  useEffect(() => {
    if (nameFieldsRef.current) {
      setNameFieldsHeight(nameFieldsRef.current.scrollHeight);
    }
  }, [showMoreFields]);

  useEffect(() => {
    if (!showIntro) {
      setNameFieldsVisible(true);
    }
  }, [showIntro]);

  useEffect(() => {
    if (!showIntro && topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight || 0);
    } else if (showIntro) {
      setTopBarHeight(0);
    }
  }, [showIntro, showMoreFields, horizontalPadding]);

  // Scroll to appropriate section when toggling between field groups
  useEffect(() => {
    if (showMoreFields) {
      if (additionalFieldsRef.current) {
        additionalFieldsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      if (nameFieldsRef.current) {
        nameFieldsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [showMoreFields]);

  // Reset to name fields after a failed response so the user can try again
  useEffect(() => {
    if (hasReceivedFailedResponse) {
      if (!shouldUseSinglePage) {
        setShowMoreFields(false);
        if (nameFieldsRef.current) {
          nameFieldsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      form.clearErrors();
      setShouldShowRetryAlert(true);
      setHasReceivedFailedResponse(false);
    }
  }, [hasReceivedFailedResponse, shouldUseSinglePage]);

  useEffect(() => {
    if (shouldShowRetryAlert && !isVerifying) {
      notifications.show({
        id: retryNotificationId,
        title: locale?.retry?.title || "Whoops! That did not go as expected",
        message:
          locale?.retry?.message || "Please check that you've entered your full legal name, and provided an accurate date of birth or SSN.",
        color: "red",
        withCloseButton: true,
        autoClose: 5000,
      });
      setShouldShowRetryAlert(false);
    }
  }, [shouldShowRetryAlert, isVerifying, retryNotificationId]);

  // Handle ResizeObserver for dynamic height calculations
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      if (additionalFieldsRef.current) {
        setAdditionalHeight(additionalFieldsRef.current.scrollHeight);
      }
      if (nameFieldsRef.current) {
        setNameFieldsHeight(nameFieldsRef.current.scrollHeight);
      }
    });

    if (additionalFieldsRef.current) observer.observe(additionalFieldsRef.current);
    if (nameFieldsRef.current) observer.observe(nameFieldsRef.current);

    return () => observer.disconnect();
  }, [showMoreFields]);

  // Edge mode status handler
  const handleEdgeStatusResult = (statusResult) => {
    if (!statusResult) return true;

    setStatusResponse(statusResult);

    const {
      status,
      process_state: processState,
      should_continue_polling: shouldContinuePolling,
      attempts_used: attemptsUsed,
      attempts_allowed: attemptsAllowed,
    } = statusResult;

    if (typeof attemptsUsed === "number") {
      const nextAttempt = attemptsAllowed
        ? Math.min(attemptsUsed + 1, attemptsAllowed)
        : attemptsUsed + 1;
      setCurrentAttempt(nextAttempt);
    }

    if (shouldContinuePolling) {
      setLoading(true);
      return true;
    }

    if (processState === "WAITING") {
      if (waitingForConfirmationChangeRef.current) {
        waitingForConfirmationChangeRef.current = false;
      }

      try {
        dispatch({ type: "SET_STATUS", payload: "PENDING" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
      } catch (err) {
        // Silent fail
      }

      setHasShownFormForCurrentAttempt(true);
      setIsVerifying(false);
      setLoading(false);
      setHasReceivedFailedResponse(true);
      setShouldShowRetryAlert(true);
      setResult(false);

      return false;
    }

    if (processState === "COMPLETED" || status === "PASS" || status === "FAIL") {
      if (status === "PASS") {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        setLoading(false);
        setIsVerifying(false);
        return false;
      }

      dispatch({ type: "SET_STATUS", payload: "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      setLoading(false);
      setIsVerifying(false);

      return false;
    }

    return true;
  };

  // Standard status check handler
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.status === "FAIL" && !hasShownFormForCurrentAttempt && initialLoadRef.current) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setIsVerifying(false);
      setLoading(false);
      setResult(false);
      return false;
    }

    if (statusResult.status === "PASS") {
      try {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      } catch (err) {
        // Silent fail
      }
      setIsVerifying(false);
      setLoading(true);
      return true;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS") {
        setIsVerifying(false);
        setLoading(false);
        setHasReceivedFailedResponse(true);
        setShouldShowRetryAlert(true);
        setResult(false);
        return false;
      }

      setLoading(true);
      return true;
    }

    try {
      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
    } catch (err) {
      // Silent fail
    }

    setLoading(true);

    if (!result) {
      setResult(true);
    }

    return true;
  };

  // Determine if we should use edge mode
  const isEdgeMode = data?.edge === true;

  // Status check hooks
  useStatusCheck(!isEdgeMode && result, false, componentKey, handleStatusResult);
  useEdgeStatusCheck(isEdgeMode && result, handleEdgeStatusResult);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, {}),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      config.fields.forEach((field) => {
        if (field.validation) {
          // Skip validation for DOB and SSN until "Verify my identity" is clicked
          if (!showMoreFields && (field.name === "dob" || field.name === "ssn")) {
            return;
          }

          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Submit handler
  const submitHandler = async () => {
    // Send postMessage for verification initiated
    if (postPayloadMessage) {
      postPayloadMessage({
        event: "VERIFICATION_INITIATED",
        app: componentKey,
      });
    }

    setIsVerifying(true);
    setShouldShowRetryAlert(false);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    Object.keys(form.values).forEach((key) => {
      data[key] = form.values[key];
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;

    setLoading(true);

    try {
      let apiUrl;
      let requestData = { ...data };

      if (data?.edge === true) {
        const edgeQueueUrl =
          process.env.REACT_APP_EDGE_QUEUE_URL ||
          "https://dcams.app/edge/frontend/api/queue/batch";
        apiUrl = edgeQueueUrl;
        requestData.queue = "coppa";
      } else {
        apiUrl = "https://vx.dcams.app/frontend/idmatch/coppa";
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then((res) => res.json())
        .then((responseData) => {
          if (typeof onApiResponse === "function") {
            onApiResponse(responseData);
          }

          try {
            dispatch({ type: "SET_STATUS", payload: "PENDING" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
          } catch (err) {
            // Silent fail
          }

          const shouldSkipVerificationLoader =
            isEdgeMode && responseData?.is_overloaded === false;

          if (shouldSkipVerificationLoader) {
            setLoading(false);
          } else {
            setTimeout(() => {
              setLoading(true);
              setIsVerifying(false);
            }, 2000);
          }

          setCurrentAttempt((prevAttempt) => prevAttempt + 1);

          if (isEdgeMode) {
            setHasShownFormForCurrentAttempt(false);
          }

          waitingForConfirmationChangeRef.current = true;
          if (shouldSkipVerificationLoader) {
            setResult(true);
          }
          // When the queue is overloaded we let the loader finish before starting status checks
        });
    } catch (error) {
      alert(locale?.form?.somethingWentWrong || "Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };


  const handleDobChange = (dob) => {
    if (!dob) {
      return;
    }

    const formattedDob = normalizeDob(`${dob.year || ""}${dob.month || ""}${dob.day || ""}`);
    const isCompleteDob = formattedDob.length === 8;
    const isEmptyDob = formattedDob.length === 0;

    if (isCompleteDob) {
      form.setFieldValue("dob", formattedDob);
    } else if (isEmptyDob) {
      form.setFieldValue("dob", "");
    }
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setLoading(false);
    form.clearErrors();
    setShouldShowRetryAlert(false);
  }, [componentKey]);

  // If status is PASS or FAIL, don't render the component - let DataContext show result screen
  if (status === "PASS" || status === "FAIL") {
    return null;
  }

  if (loading && !isVerifying) {
    return <LoadingOverlay visible />;
  }

  if (shouldUseSinglePage) {
    return (
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
        }}
      >
        <Stack spacing="md" style={{ flex: 1 }}>
          <Box px={horizontalPadding} pt={isMobileSinglePage ? "md" : "sm"}>
            <Stack spacing={isMobileSinglePage ? 12 : 4} align="stretch">
              {(headerIcon || title) && (
                <Group
                  spacing={8}
                  wrap="nowrap"
                  style={{ width: "100%" }}
                  align="center"
                  justify="flex-start"
                >
                  {headerIcon && (
                    <Box aria-hidden style={{ display: "flex", alignItems: "center" }}>
                      {headerIcon}
                    </Box>
                  )}
                  {title && (
                    <Text
                      fw={isMobileSinglePage ? 700 : 600}
                      size={isMobileSinglePage ? "28px" : "lg"}
                      style={{ lineHeight: 1.2 }}
                    >
                      {title}
                    </Text>
                  )}
                </Group>
              )}
              {subtitle && (
                <Text
                  size={isMobileSinglePage ? "lg" : "sm"}
                  c="dimmed"
                  style={{ lineHeight: 1.4 }}
                >
                  {subtitle}
                </Text>
              )}
            </Stack>
          </Box>

          {Object.keys(groupedFields.grouped).map((group) => (
            <Box key={group} px={horizontalPadding}>
              <Stack spacing="md">
                {groupedFields.grouped[group].map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    form={form}
                    settings={settings}
                    componentKey={componentKey}
                    showMoreFields
                    onDobChange={handleDobChange}
                    onAddressChange={handleAddressChange}
                    isSinglePageForm
                  />
                ))}
              </Stack>
            </Box>
          ))}

          <Box px={horizontalPadding}>
            <Stack spacing="md">
              {groupedFields.ungrouped.map((field) => (
                <FormField
                  key={field.name}
                  field={field}
                  form={form}
                  settings={settings}
                  componentKey={componentKey}
                  showMoreFields
                  onDobChange={handleDobChange}
                  onAddressChange={handleAddressChange}
                  isSinglePageForm
                />
              ))}
            </Stack>
          </Box>
        </Stack>

        <Box pt={isMobileSinglePage ? "lg" : "md"} px={horizontalPadding}>
          <Button
            fullWidth
            size="lg"
            type="submit"
            disabled={isVerifying}
            sx={{
              transition: `all 0.5s ${bounceTransition}`,
              "&:hover": { transform: "scale(1.01)" },
              '&[data-loading="true"]': { color: buttonContentColor },
            }}
          >
            {isVerifying ? (
              <MantineGroup spacing="xs" noWrap>
                <Loader size="xs" color={buttonContentColor} />
                <Text c="inherit">{locale?.form?.verifying || "Verifying..."}</Text>
              </MantineGroup>
            ) : (
              buttonText
            )}
          </Button>
        </Box>
      </form>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideUpBounce {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            60% {
              opacity: 1;
              transform: translateY(-5px);
            }
            80% {
              transform: translateY(3px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      {!showIntro && (
        <Box
          ref={topBarRef}
          sx={{
            position: "fixed",
            top: `calc(${NAVIGATION_HEIGHT}px + env(safe-area-inset-top))`,
            left: 0,
            right: 0,
            zIndex: 2000,
            backgroundColor:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
            borderBottom: `1px solid ${
              theme.colorScheme === "dark"
                ? theme.colors.dark[5]
                : theme.colors.gray[3]
            }`,
            padding: "4px 0",
          }}
        >
          <Box
            sx={{
              maxWidth: "500px",
              margin: "0 auto",
              paddingLeft: `${horizontalPadding}px`,
              paddingRight: `${horizontalPadding}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              fz="sm"
              fw={700}
              ta="center"
              c={theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.black}
            >
              {showMoreFields ? "Date of birth & SSN" : "Name details"}
            </Text>
          </Box>
        </Box>
      )}

      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          animation: "fadeIn 0.6s ease-out",
          minHeight: "100%",
          maxWidth: "500px",
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
          paddingLeft: `${horizontalPadding}px`,
          paddingRight: `${horizontalPadding}px`,
          paddingBottom: isMobileDevice
            ? "calc(56px + env(safe-area-inset-bottom))"
            : "calc(80px + env(safe-area-inset-bottom))",
          marginTop: !showIntro ? topBarHeight : 0,
        }}
      >
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showIntro && (
            <DataCollectionIntro
              icon={icon}
              title={title}
              subtitle={subtitle}
              componentKey={componentKey}
              inputSize={inputSize}
              data={data}
              onStart={() => setShowIntro(false)}
              bounceTransition={bounceTransition}
              theme={theme}
              postPayloadMessage={postPayloadMessage}
            />
          )}

          {!showIntro && (
              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 0,
                  width: "100%",
                }}
              >
                <Stack
                  spacing={showMoreFields ? 0 : "md"}
                  style={{ flexGrow: 1, flex: 1, width: "100%", display: "flex", flexDirection: "column" }}
                >
                {Object.keys(groupedFields.grouped).map((group) => (
                  <Stack key={group} spacing="md" style={{ width: "100%" }}>
                    {groupedFields.grouped[group].map((field) => (
                      <FormField
                        key={field.name}
                        field={field}
                        form={form}
                        settings={settings}
                        componentKey={componentKey}
                        showMoreFields={showMoreFields}
                        onDobChange={handleDobChange}
                        onAddressChange={handleAddressChange}
                      />
                    ))}
                  </Stack>
                ))}

                {/* Name fields - hide when expanded */}
                <div
                  ref={nameFieldsRef}
                  style={{
                    overflow: "hidden",
                    maxHeight: showMoreFields
                      ? 0
                      : nameFieldsVisible
                        ? nameFieldsHeight || "none"
                        : 0,
                    opacity: showMoreFields ? 0 : nameFieldsVisible ? 1 : 0,
                    transform: showMoreFields
                      ? "translateY(-20px)"
                      : nameFieldsVisible
                        ? "translateY(0)"
                        : "translateY(20px)",
                    transition: `max-height 0.5s ${bounceTransition}, opacity 0.5s ${bounceTransition}, transform 0.5s ${bounceTransition}`,
                    animation:
                      !showMoreFields && nameFieldsVisible
                        ? `slideUpBounce 0.5s ${bounceTransition}`
                        : "none",
                    pointerEvents: showMoreFields ? "none" : "auto",
                  }}
                >
                  <Stack spacing="md">
                    {primaryFields.map((field) => (
                      <FormField
                        key={field.name}
                        field={field}
                        form={form}
                        settings={settings}
                        componentKey={componentKey}
                        showMoreFields={showMoreFields}
                        onDobChange={handleDobChange}
                        onAddressChange={handleAddressChange}
                      />
                    ))}
                  </Stack>
                </div>

                {/* Expandable container that grows from button */}
                {additionalFields.length > 0 && (
                  <div
                    style={{
                      overflow: "hidden",
                      maxHeight: showMoreFields ? additionalHeight : 0,
                      opacity: showMoreFields ? 1 : 0,
                      transform: showMoreFields ? "translateY(0)" : "translateY(20px)",
                      transition: `max-height 0.5s ${bounceTransition}, opacity 0.5s ${bounceTransition}, transform 0.5s ${bounceTransition}`,
                      animation: showMoreFields
                        ? `slideUpBounce 0.5s ${bounceTransition}`
                        : "none",
                      pointerEvents: showMoreFields ? "auto" : "none",
                    }}
                  >
                    <div ref={additionalFieldsRef}>
                      <Stack spacing="md">
                        {additionalFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={showMoreFields}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                        
                        {/* Back button when showing additional fields */}
                        <div
                          style={{
                            textAlign: "center",
                            paddingTop: "20px",
                            transform: showMoreFields ? "scale(1)" : "scale(0.9)",
                            opacity: showMoreFields ? 1 : 0,
                            transition: `opacity 0.5s ${bounceTransition}, transform 0.5s ${bounceTransition}`,
                            pointerEvents: showMoreFields ? "auto" : "none",
                          }}
                        >
                          <Button
                            variant="subtle"
                            size="sm"
                            onClick={() => setShowMoreFields(false)}
                            sx={{
                              color:
                                theme.colorScheme === "dark"
                                  ? theme.colors.gray[3]
                                  : theme.colors.gray[7],
                              "&:hover": {
                                backgroundColor:
                                  theme.colorScheme === "dark"
                                    ? theme.colors.dark[6]
                                    : theme.colors.gray[0],
                              },
                            }}
                            styles={{
                              root: {
                                fontSize: "13px",
                                height: "32px",
                                padding: "0 16px",
                              },
                            }}
                          >
                            ← Back
                          </Button>
                        </div>
                      </Stack>
                    </div>
                  </div>
                )}
              </Stack>

              {/* Button positioned at bottom */}
              <Box
                style={{
                  paddingTop: "15px",
                  marginBottom: isMobileDevice ? "32px" : showIntro ? "24px" : "8px",
                  width: "100%",
                  alignSelf: "stretch",
                }}
              >
                <Button
                  fullWidth
                  size="lg"
                  type={showMoreFields ? "submit" : "button"}
                  disabled={
                    isVerifying ||
                    (!showMoreFields && !isNameFieldsValid()) ||
                    (showMoreFields && !isCoppaDetailsValid())
                  }
                  loading={false}
                  onClick={(e) => {
                    if (!showMoreFields) {
                      e.preventDefault();
                      setShowMoreFields(true);
                    }
                  }}
                  sx={{
                    transition: `all 0.5s ${bounceTransition}`,
                    "&:hover": { transform: "scale(1.01)" },
                    '&[data-loading="true"]': { color: buttonContentColor },
                  }}
                >
                  {isVerifying ? (
                    <MantineGroup spacing="xs" noWrap>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: `2px solid ${buttonContentColor}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Text c="inherit">{locale?.form?.verifying || "Verifying..."}</Text>
                    </MantineGroup>
                  ) : showMoreFields ? (
                    buttonText
                  ) : (
                    "Add more details"
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </form>
    </>
  );
};

export default CoppaForm;
